import React, { useEffect, useMemo, useState } from "react";
import {
  getAllMessagesContactForm,
  markMessageContactForm,
  deleteMessageContactForm,
} from "../../../../services/contactformServices";
import { toast } from "react-toastify";
import { useContactForm } from "../../../../contexts/ContactFormContext";
import ButtonDelete from "../../../../components/Button/ButtonDelete";
import ButtonEnvelopeToggle from "../../../../components/Button/ButtonEnvelopeToggle";
import { FaEnvelope, FaSearch } from "react-icons/fa";

const fmtDate = (d) => {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(d));
  } catch {
    return "";
  }
};

const MessagesSection = () => {
  const [messages, setMessages] = useState([]);
  const { unreadCountContactForm, setUnreadCountContactForm } = useContactForm();

  const [isDemoMode, setIsDemoMode] = useState(false);
  const [q, setQ] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);

  const card =
    "rounded-3xl border border-black/10 bg-white/70 shadow-sm backdrop-blur-sm " +
    "dark:bg-slate-950/35 dark:border-white/10 dark:shadow-none";

  const softText = "text-slate-900 dark:text-white";
  const mutedText = "text-slate-600 dark:text-slate-300";

  const input =
    "w-full rounded-2xl px-3 py-2 text-sm outline-none transition " +
    "border border-black/10 dark:border-white/10 " +
    "bg-white/70 dark:bg-black/20 " +
    "text-slate-900 dark:text-white " +
    "focus:ring-2 focus:ring-primary/60 focus:border-transparent";

  const checkUserRole = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.role_id === 4) setIsDemoMode(true);
    } catch {
      // ignore
    }
  };

  const loadMessages = async () => {
    try {
      const data = await getAllMessagesContactForm();
      setMessages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur récupération messages :", error);
      toast.error("Erreur lors de la récupération des messages.");
    }
  };

  useEffect(() => {
    loadMessages();
    checkUserRole();
  }, []);

  const handleDemoClick = () => {
    if (isDemoMode) {
      toast.info("Fonction désactivée en mode Démonstration");
      return true;
    }
    return false;
  };

  const toggleReadStatus = async (messageId) => {
    if (handleDemoClick()) return;

    try {
      const message = messages.find((m) => m.id === messageId);
      if (!message) return;

      await markMessageContactForm(messageId);

      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, read_message: !m.read_message } : m
        )
      );

      setUnreadCountContactForm((prevCount) =>
        message.read_message ? prevCount + 1 : Math.max(0, prevCount - 1)
      );
    } catch (error) {
      console.error("Erreur toggle read:", error);
      toast.error("Erreur lors du changement de statut.");
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (handleDemoClick()) return;

    try {
      await deleteMessageContactForm(messageId);
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
      toast.success("Message supprimé avec succès.");
    } catch (error) {
      console.error("Erreur suppression :", error);
      toast.error("Erreur lors de la suppression du message.");
    }
  };

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();

    const arr = (messages || [])
      .slice()
      .sort((a, b) => {
        // Non lus en haut
        const ra = a.read_message ? 1 : 0;
        const rb = b.read_message ? 1 : 0;
        if (ra !== rb) return ra - rb;
        // plus récent ensuite
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      })
      .filter((m) => {
        if (unreadOnly && m.read_message) return false;
        if (!qq) return true;
        const hay = `${m.name || ""} ${m.email || ""} ${m.message || ""}`.toLowerCase();
        return hay.includes(qq);
      });

    return arr;
  }, [messages, q, unreadOnly]);

  return (
    <div className={`${card} p-4 sm:p-5`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div
                className="
                  h-11 w-11 rounded-2xl flex items-center justify-center
                  bg-primary/10 text-primary border border-primary/20
                "
                title="Messages"
              >
                <FaEnvelope className="text-lg" />
              </div>

              {unreadCountContactForm > 0 && (
                <span
                  className="
                    absolute -top-2 -right-2
                    rounded-full bg-primary text-white
                    text-xs font-extrabold
                    px-2 py-0.5
                    shadow
                  "
                >
                  {unreadCountContactForm}
                </span>
              )}
            </div>

            <div className="min-w-0">
              <h3 className={`text-base sm:text-lg font-extrabold ${softText}`}>
                Messages (Contact)
              </h3>
              <p className={`text-xs ${mutedText}`}>
                {filtered.length} message(s)
                {unreadCountContactForm > 0 ? ` • ${unreadCountContactForm} non lu(s)` : ""}
              </p>
            </div>
          </div>
        </div>

        <label className="inline-flex items-center gap-2 text-sm select-none cursor-pointer">
          <input
            type="checkbox"
            className="h-5 w-5 accent-primary"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
          />
          <span className={`font-semibold ${softText}`}>Non lus</span>
        </label>
      </div>

      {/* Search */}
      <div className="mt-4 flex items-center gap-2">
        <div className="relative w-full">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className={`${input} pl-9`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (nom, email, message)…"
          />
        </div>
      </div>

      {/* Content */}
      <div className="mt-4">
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20 p-5">
            <p className={`text-center text-sm ${mutedText}`}>Pas encore de messages.</p>
          </div>
        ) : (
          <>
            {/* Mobile: cards */}
            <div className="space-y-3 md:hidden">
              {filtered.map((m) => (
                <div
                  key={m.id}
                  className={`
                    rounded-2xl border p-4
                    ${m.read_message
                      ? "border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5"
                      : "border-primary/25 bg-primary/5"}
                  `}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={`text-sm font-extrabold ${softText}`}>
                        {m.name || "—"}
                      </div>
                      <div className={`text-xs ${mutedText}`}>{fmtDate(m.created_at)}</div>
                      <div className={`mt-2 text-xs ${mutedText} break-words`}>
                        <span className="font-semibold">Email :</span>{" "}
                        <a className="underline text-primary" href={`mailto:${m.email}`}>
                          {m.email}
                        </a>
                      </div>
                      <div className={`mt-2 text-sm ${softText} break-words`}>
                        {String(m.message || "").slice(0, 180)}
                        {String(m.message || "").length > 180 ? "…" : ""}
                      </div>
                    </div>

                    <div className="shrink-0 flex items-center gap-2">
                      <ButtonEnvelopeToggle
                        isRead={m.read_message}
                        onClick={() => toggleReadStatus(m.id)}
                      />
                      <ButtonDelete onClick={() => handleDeleteMessage(m.id)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-hidden rounded-2xl border border-black/10 dark:border-white/10">
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white/60 dark:bg-black/20">
                  <thead className="bg-black/5 dark:bg-white/5">
                    <tr>
                      <th className={`px-3 py-3 text-left text-xs font-extrabold uppercase ${mutedText}`}>
                        Date
                      </th>
                      <th className={`px-3 py-3 text-left text-xs font-extrabold uppercase ${mutedText}`}>
                        Message
                      </th>
                      <th className={`px-3 py-3 text-right text-xs font-extrabold uppercase ${mutedText}`}>
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-black/10 dark:divide-white/10">
                    {filtered.map((m) => (
                      <tr
                        key={m.id}
                        className={`
                          transition
                          hover:bg-black/5 dark:hover:bg-white/5
                          ${m.read_message ? "opacity-90" : ""}
                        `}
                      >
                        <td className={`px-3 py-3 whitespace-nowrap text-sm ${softText}`}>
                          {fmtDate(m.created_at)}
                        </td>

                        <td className="px-3 py-3">
                          <div className={`text-sm font-extrabold ${softText}`}>
                            {m.name || "—"}
                          </div>
                          <div className={`text-xs ${mutedText}`}>
                            <a className="underline text-primary" href={`mailto:${m.email}`}>
                              {m.email}
                            </a>
                          </div>
                          <div className={`mt-2 text-sm ${mutedText}`}>
                            {String(m.message || "").slice(0, 140)}
                            {String(m.message || "").length > 140 ? "…" : ""}
                          </div>
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex justify-end items-center gap-2">
                            <ButtonEnvelopeToggle
                              isRead={m.read_message}
                              onClick={() => toggleReadStatus(m.id)}
                            />
                            <ButtonDelete onClick={() => handleDeleteMessage(m.id)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MessagesSection;
